import { format } from "date-fns";
import { IRotaManagementShiftTimings, IRotaOverbookingEligibleService, IRotaOverbookingRequest } from "@/api_framework/api_modals/Rota";

export interface IAppointmentOverBookingV2ModalFields {
  clinicId: string;
  clinicName: string;
  date: string;
  type: string;
  providerId: string;
  providerName: string;
  shift: string;
  startTimeHour: string;
  startTimeMinute: string;
  endTimeHour: string;
  endTimeMinute: string;
}

export interface IOverbookingRequestRouteLocationState {
  requestData?: IRotaOverbookingRequest;
  listUrl?: string;
  initialFilters?: {
    clinicId?: string;
    date?: string;
    type?: string;
  };
}

export interface ITimeSegment {
  startTimeHour: string;
  startTimeMinute: string;
  endTimeHour: string;
  endTimeMinute: string;
}

export const DEFAULT_DATE = format(new Date(), "yyyy-MM-dd");

export const DEFAULT_FORM_VALUES: IAppointmentOverBookingV2ModalFields = {
  clinicId: "",
  clinicName: "",
  date: "",
  type: "",
  providerId: "",
  providerName: "",
  shift: "",
  startTimeHour: "",
  startTimeMinute: "",
  endTimeHour: "",
  endTimeMinute: "",
};

export const normalizeRequestData = (requestData?: IRotaOverbookingRequest | null) => {
  const request = (requestData as any) ?? {};
  return {
    requestDate: request.request_date || request.date || request.ob_date || DEFAULT_DATE,
    clinicId: requestData?.clinic_id || request?.clinic?.id || "",
    clinicName: request.clinic_details?.name || request.clinic_name || request?.clinic?.name || "",
    type: request.service_type || request.type || "",
    shiftId: requestData?.shift_id || request?.shift?.id || "",
    providerId: request.staff_details?.id || request.staff_id || request?.staff?.id || "",
    providerName: request.staff_details?.name || request.staff_name || request?.staff?.name || "",
    warnContext: {
      clinicId: request.clinic_details?.id || requestData?.clinic_id || "",
      staffId: request.staff_details?.id || request.staff_id || request?.staff?.id || "",
      rotaDate: request.date || request.request_date || request.ob_date || "",
      rotaId: request.rota_details?.id || "",
    },
    requestId: requestData?.id || requestData?.request_id || "",
  };
};

export const buildServiceTypeItems = (services: Array<IRotaOverbookingEligibleService | string> = []) => {
  const uniqueServiceTypes = new Map<string, string>();
  services?.forEach((service) => {
    const typeValue = typeof service === "string" ? service : service?.service_type || service?.name || service?.id || "";
    if (!typeValue) return;
    uniqueServiceTypes.set(typeValue, typeValue);
  });
  return Array.from(uniqueServiceTypes.keys())?.map((serviceType) => ({
    id: serviceType,
    title: serviceType,
  }));
};

const getTimeInMinutes = (hour: string, minute: string) => {
  if (!hour || !minute) return null;
  const hh = Number.parseInt(hour, 10);
  const mm = Number.parseInt(minute, 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

export const isTimeRangeValid = (startHour: string, startMinute: string, endHour: string, endMinute: string) => {
  const start = getTimeInMinutes(startHour, startMinute);
  const end = getTimeInMinutes(endHour, endMinute);
  if (start === null || end === null) return false;
  let duration = end - start;
  if (duration <= 0) {
    duration += 24 * 60;
  }
  const MAX_ADHOC_DURATION_MINUTES = 12 * 60;
  return duration > 0 && duration <= MAX_ADHOC_DURATION_MINUTES;
};

export const buildTimingsFromSegments = (segments: ITimeSegment[]): IRotaManagementShiftTimings[] =>
  segments.map((segment) => ({
    start_time: `${segment.startTimeHour}:${segment.startTimeMinute}`,
    end_time: `${segment.endTimeHour}:${segment.endTimeMinute}`,
  }));
